import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, Mountain, Zap, Heart } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { useTournamentsQuery } from '@/services/endpoints/tournaments';
import { useAppSelector } from '@/store/hooks';
import { useGameSelection } from '@/hooks/useGameSelection';
import { useHaptics } from '@/hooks/useHaptics';
import { TournamentCard } from '@/components/tournaments/TournamentCard';
import { GameHeader } from '@/components/games/GameHeader';
import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';
import { CR_GAME_ID } from '@/lib/gameFeatures';
import type { Tournament } from '@/types';
import { TOURNAMENT_STATUS } from '@/types/enums';

const CLOSED_STATUSES = new Set<string>([
  TOURNAMENT_STATUS.FINISHED,
  TOURNAMENT_STATUS.CLOSED,
  TOURNAMENT_STATUS.REGISTRATION_CLOSED,
]);
const STALE_DAYS = 7;

function isActiveTournament(t: Tournament): boolean {
  if (CLOSED_STATUSES.has(t.status)) return false;
  // Backend leaves CHECKIN/STARTED/IN_PROGRESS forever even after the event is
  // over — filter by started_at age so the list isn't polluted by months-old
  // ghosts. Only OPENED/REGISTRATION_OPENED/PENDING (registration phase before
  // start) are allowed to live in the future regardless of age.
  const futureOk =
    t.status === TOURNAMENT_STATUS.OPENED ||
    t.status === TOURNAMENT_STATUS.REGISTRATION_OPENED ||
    t.status === TOURNAMENT_STATUS.PENDING;
  if (futureOk) return true;
  const startedMs = new Date(t.started_at).getTime();
  if (Number.isNaN(startedMs)) return true;
  return Date.now() - startedMs <= STALE_DAYS * 24 * 60 * 60 * 1000;
}

type ViewKey = 'tournaments' | 'challenges' | 'sitAndGo' | 'community';

const REGULAR_TYPES = '1,2,3,4,5,6,7,9';
const SIT_AND_GO_TYPE = 12;
const CHALLENGE_TYPE = 8;
const COMMUNITY_TAG = 'christmas25';

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function Tournaments() {
  const { t } = useTranslation('tournaments');
  const haptics = useHaptics();
  const activeGames = useAppSelector((s) => s.core.activeGames);
  const { activeGame } = useGameSelection();
  const gamesParam = activeGames.length ? activeGames.join(',') : undefined;

  const isCR = activeGame?.id === CR_GAME_ID;

  // Default view is "tournaments". Reset when the active game changes since
  // the available secondary tab swaps between Sit&Go (CR) and Challenges.
  const [view, setView] = useState<ViewKey>('tournaments');
  useEffect(() => {
    setView('tournaments');
  }, [activeGame?.id]);

  // refetchOnMountOrArgChange + refetchOnFocus + short poll keep the seat
  // count from going stale: backend bumps participant_count as users join and
  // when a tournament transitions OPEN → CHECKIN → STARTED. Without this RTK
  // Query would pin to the original cached number until the user manually
  // reloads. 8s is aggressive enough to surface a CHECKIN→STARTED bump
  // within a single Mini App session without hammering the Laravel API.
  const POLL_MS = 8000;
  const queryOpts = {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    pollingInterval: POLL_MS,
  } as const;
  const tournamentsQuery = useTournamentsQuery(
    { limit: 50, games: gamesParam, tournament_type: REGULAR_TYPES },
    queryOpts,
  );
  const challengesQuery = useTournamentsQuery(
    { limit: 50, games: gamesParam, tournament_type: CHALLENGE_TYPE },
    { ...queryOpts, skip: isCR },
  );
  const sitAndGoQuery = useTournamentsQuery(
    { limit: 100, games: gamesParam, tournament_type: SIT_AND_GO_TYPE },
    { ...queryOpts, skip: !isCR },
  );
  const communityQuery = useTournamentsQuery(
    { limit: 50, games: gamesParam, tournament_type: REGULAR_TYPES, tags: COMMUNITY_TAG },
    queryOpts,
  );

  const tournamentNumber = useMemo(() => {
    const today = new Date();
    return (
      tournamentsQuery.data?.data.filter((tour) =>
        isSameDay(new Date(tour.started_at), today),
      ).length ?? 0
    );
  }, [tournamentsQuery.data]);

  const challengeNumber = useMemo(
    () => (challengesQuery.data?.data ?? []).filter(isActiveTournament).length,
    [challengesQuery.data],
  );
  const communityNumber = useMemo(
    () => (communityQuery.data?.data ?? []).filter(isActiveTournament).length,
    [communityQuery.data],
  );
  const sitAndGoNumber = useMemo(() => {
    return (
      sitAndGoQuery.data?.data.filter((tour) => {
        const open = tour.status === 'REGISTRATION_OPENED' || tour.status === 'CHECKIN';
        const notFull = tour.registered_participant_count < tour.max_member;
        return open && notFull;
      }).length ?? 0
    );
  }, [sitAndGoQuery.data]);

  const activeQuery = useMemo(() => {
    switch (view) {
      case 'sitAndGo':
        return sitAndGoQuery;
      case 'challenges':
        return challengesQuery;
      case 'community':
        return communityQuery;
      case 'tournaments':
      default:
        return tournamentsQuery;
    }
  }, [view, tournamentsQuery, challengesQuery, sitAndGoQuery, communityQuery]);

  const tournaments: Tournament[] = useMemo(
    () => (activeQuery.data?.data ?? []).filter(isActiveTournament),
    [activeQuery.data],
  );
  const isLoading = activeQuery.isLoading;
  const isError = activeQuery.isError;

  const tabs: ReadonlyArray<TabDef> = useMemo(
    () => [
      {
        key: 'tournaments',
        Icon: Trophy,
        label: t('tabs.tournaments', { defaultValue: 'Tournaments' }),
        counter: tournamentNumber,
      },
      isCR
        ? {
            key: 'sitAndGo',
            Icon: Zap,
            label: t('tabs.sitAndGo', { defaultValue: 'Sit&Go' }),
            counter: sitAndGoNumber,
          }
        : {
            key: 'challenges',
            Icon: Mountain,
            label: t('tabs.challenges', { defaultValue: 'Challenges' }),
            counter: challengeNumber,
          },
      {
        key: 'community',
        Icon: Heart,
        label: t('tabs.community', { defaultValue: 'Community' }),
        counter: communityNumber,
      },
    ],
    [t, isCR, tournamentNumber, sitAndGoNumber, challengeNumber, communityNumber],
  );

  const switchView = (next: ViewKey) => {
    if (next === view) return;
    haptics.selection();
    setView(next);
  };

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">{t('list', { defaultValue: 'Tournaments' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('subtitle', { defaultValue: 'Compete in your favourite games' })}
        </p>
      </header>

      <GameHeader />

      <div role="tablist" className="grid grid-cols-3 gap-2">
        {tabs.map((tab) => (
          <CategoryTab
            key={tab.key}
            tab={tab}
            active={view === tab.key}
            onClick={() => switchView(tab.key)}
          />
        ))}
      </div>

      {isLoading && (
        <div className="flex h-40 items-center justify-center">
          <Loader />
        </div>
      )}

      {isError && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
          {t('load_error', { defaultValue: 'Failed to load tournaments.' })}
        </div>
      )}

      {!isLoading && !isError && tournaments.length === 0 && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('empty', { defaultValue: 'No tournaments in this category.' })}
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {tournaments.map((tournament) => (
          <li key={tournament.id}>
            <TournamentCard tournament={tournament} variant="full" />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface TabDef {
  key: ViewKey;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  counter: number;
}

function CategoryTab({
  tab,
  active,
  onClick,
}: {
  tab: TabDef;
  active: boolean;
  onClick: () => void;
}) {
  const { Icon, label, counter } = tab;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-3',
        'transition-colors active:opacity-80',
        active ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted',
      )}
    >
      {counter > 0 && (
        <span
          className={cn(
            'absolute right-1.5 top-1.5 inline-flex h-4 min-w-[1.1rem] items-center justify-center',
            'rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white',
          )}
        >
          {counter > 99 ? '99+' : counter}
        </span>
      )}
      <Icon aria-hidden className="h-5 w-5" />
      <span className="text-[11px] font-bold uppercase tracking-wide">{label}</span>
    </button>
  );
}
